import { useState } from "react";
import { Editor } from "./Editor";
import { EditorSimple } from "./EditorSimple";
import { TranscriptTab } from "./TranscriptTab";
import { TranscribeButton } from "./TranscribeButton";
import { useNotes } from "../contexts/NotesContext";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { StickyNote, WandSparkles, Sparkles, ArrowLeftIcon, MoveLeft, CircleEllipsis, Trash, EllipsisVertical } from "lucide-react";
import { useCourses } from "@/lib/courses";
import { AnimatePresence, motion } from "framer-motion";
import { SmartNotesTab } from "./SmartNotesTab";
import { SmartNoteTab } from "./SmartNoteTab";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EditorYjs } from "./EditorYjs";

const NoteHeader = () => {

    const { setNoteId, handleDeleteNote } = useNotes();

    return (
        <>
            <div className="absolute top-4 left-4 z-10">
                <button onClick={() => setNoteId(null)} className="cursor-pointer py-1.5 px-2 rounded-md g-theme-1 hover:bg-theme-2 transition-all duration-200">
                    <MoveLeft className="size-4.5 text-theme-0" />
                </button>
            </div>

            <div className="absolute top-4 right-4 z-10">
                <Popover>
                    <PopoverTrigger asChild>
                        <button className="cursor-pointer py-1.5 px-2 rounded-md g-theme-1 hover:bg-theme-2 transition-all duration-200">
                            <EllipsisVertical className="size-4.5 text-theme-0" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent>
                        <div className="flex items-center gap-2">
                            <button onClick={handleDeleteNote} className="flex items-center gap-2 cursor-pointer py-1.5 px-2 rounded-md g-theme-1 hover:bg-theme-2 transition-all duration-200">
                                <Trash className="size-4.5 text-theme-0" />
                                <span className="text-sm font-medium text-theme-0">Delete Note</span>
                            </button>
                        </div>
                    </PopoverContent>
                </Popover>

            </div>
        </>
    )
}

export const Note = () => {
    const [tab, setTab] = useState('notes');
    const { activeCourse, transcriptTabOpen, notesMetadata, noteId, handleMetadataChange, setNoteId, handleDeleteNote, portal } = useNotes();
    const { courseData } = useCourses();

    const switchTab = (tab: string) => {
        setTab(tab);
    }

    const courseColor = courseData?.[activeCourse]?.color || "#6366f1";
    const courseCode = courseData?.[activeCourse]?.code || courseData?.[activeCourse]?.default || "Unknown Course";
    const courseImage = courseData?.[activeCourse]?.img || "";

    const noteMetadata = notesMetadata?.[activeCourse]?.find((note: any) => note.id === noteId) ?? null;
    const [localTitle, setLocalTitle] = useState(noteMetadata?.title || '');

    return (
        <div className="flex flex-col w-full h-full bg-theme-0 overflow-hidden relative">

            <NoteHeader />

            {/* Stunning Header with Glass Morphism */}
            <div className="relative overflow-hidden">

                <div className="relative max-w-[640px] mx-auto pt-20 pb-4">
                    {/* Course Badge with Glass Effect */}
                    <div className="flex items-center gap-3 mb-1 group">

                        <div className="flex gap-1.5 items-center">
                            <div className="size-2.5 rounded-full" style={{ backgroundColor: courseColor }}></div>
                            <p
                                className="text-sm font-bold tracking-wide transition-colors duration-200"
                                style={{ color: courseColor }}
                            >
                                {courseCode}
                            </p>
                        </div>
                    </div>

                    {/* Premium Title Input */}
                    <div className="relative mb-4 group">
                        <input
                            type="text"
                            value={localTitle}
                            onInput={(e: React.ChangeEvent<HTMLInputElement>) => setLocalTitle(e.target.value)}
                            onBlur={() => handleMetadataChange({ title: localTitle })}
                            placeholder={'Untitled Note'}
                            className="text-3xl font-bold text-theme-0 bg-transparent border-none outline-none w-full placeholder:text-theme-1/20 tracking-tight leading-tight transition-all duration-200 focus:placeholder:text-theme-1/40"
                            style={{
                                textShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}
                        />
                        <div
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-theme-1/20 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity duration-300"
                        />
                    </div>

                </div>
            </div>

            <div className="h-[1px] border-t border-theme relative">
                <div className="absolute top-0 left-0 w-full flex justify-center z-10">
                    <div className="w-full max-w-[640px] translate-y-[-50%]">
                        <NoteTabSwitcher tab={tab} switchTab={switchTab} />
                    </div>
                </div>
            </div>

            {/* Content Area with Smooth Transitions */}
            <div className="flex-1 min-h-0 relative">
                {tab === 'notes' && <EditorYjs />}
                {tab === 'smartnotes' && <SmartNoteTab />}
            </div>

            <div className="relative">
                <AnimatePresence mode="wait" initial={false}>
                    {transcriptTabOpen && (
                        <motion.div
                            key="transcript"
                            initial={{
                                height: 0,
                                opacity: 0
                            }}
                            animate={{
                                height: 320,
                                opacity: 1
                            }}
                            exit={{
                                height: 0,
                                opacity: 0
                            }}
                            transition={{
                                duration: 0.3,
                                ease: "easeInOut"
                            }}
                            className="overflow-hidden border-t border-theme"
                        >
                            <div className="h-full overflow-y-auto">
                                <TranscriptTab />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Transcribe Button with Enhanced Styling */}
                <TranscribeButton />
            </div>
        </div>
    )
}

const NoteTabSwitcher = ({ tab, switchTab }: { tab: string, switchTab: (tab: string) => void }) => {

    return (
        <div className="flex items-center justify-between">
            <RadioGroup
                value={tab}
                onValueChange={switchTab}
                className="grid grid-flow-col auto-cols-fr gap-0.5 bg-theme-1 p-[3px] rounded-xl border border-theme"
            >
                <div className="flex items-center">
                    <RadioGroupItem value="notes" id="notes" className="peer sr-only" />
                    <label
                        htmlFor="notes"
                        className={`cursor-pointer flex items-center gap-2 text-sm font-semibold px-2.5 py-2 rounded-lg text-center transition-all duration-300 ${tab === "notes"
                            ? "bg-theme-0 shadow-lg text-theme-0"
                            : "text-theme-1 hover:text-theme-0"
                            }`}
                    >
                        <StickyNote className="size-4" />
                        <span className="hidden">Notes</span>
                    </label>
                </div>
                <div className="flex items-center">
                    <RadioGroupItem value="smartnotes" id="smartnotes" className="peer sr-only" />
                    <label
                        htmlFor="smartnotes"
                        className={`cursor-pointer flex items-center gap-2 text-sm font-semibold px-2.5 py-2 rounded-lg text-center transition-all duration-300 ${tab === "smartnotes"
                            ? "bg-theme-0 shadow-lg text-theme-0"
                            : "text-theme-1 hover:text-theme-0"
                            }`}
                    >
                        <WandSparkles className="size-4" />
                        <span className="hidden">SmartNote</span>
                    </label>
                </div>
            </RadioGroup>
        </div>
    )
}